import React, { useState, useEffect, useMemo } from "react";
import { message,Spin } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import MyQuillWithUpload from "./QuillUpload";
const PRIMARY_COLOR = "#1677ff";
const BUTTON_STYLE = {
  minWidth: 120,
  minHeight: 40,
  fontWeight: 600,
  borderRadius: 8,
  fontSize: 16,
  marginLeft: 10,
  transition: "all 0.2s",
};

export default function CreateLessonModal({
  show,
  onClose,
  onSubmit,
  sectionName,
  isEdit = false,
  initialValues = {},
  loading = false,
}) {
  const [type, setType] = useState("html");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isPreview, setIsPreview] = useState(false);
  const [htmlContent, setHtmlContent] = useState("");
  const [videoFile, setVideoFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [isBlobUrl, setIsBlobUrl] = useState(false);
  useEffect(() => {
    if (!show) return;
    if (isEdit && initialValues) {
      // Normalize type from backend (HTML/Video) to lowercase for UI
      const normalizedType = initialValues.type ? initialValues.type.toLowerCase() : "html";
      setType(normalizedType);
      setName(initialValues.title || initialValues.name || "");
      setDescription(initialValues.description || "");
      setIsPreview(!!initialValues.isPreview);
      setHtmlContent(initialValues.htmlContent || "");
      setVideoFile(null);
    } else {
      setType("html");
      setName("");
      setDescription("");
      setIsPreview(false);
      setHtmlContent("");
      setVideoFile(null);
    }
    setErrors({});
  }, [show, isEdit, initialValues?.id, initialValues?.type, initialValues?.title, initialValues?.name, initialValues?.description, initialValues?.isPreview, initialValues?.htmlContent]);

  // Tạo URL cho video chỉ khi file đổi, tránh reload liên tục
  const videoUrl = useMemo(() => {
    if (videoFile) {
      return URL.createObjectURL(videoFile);
    }
    if (initialValues?.video) return initialValues.video;
    return "";
  }, [videoFile, initialValues?.video]);
  
  // Update isBlobUrl when videoFile changes
  useEffect(() => {
    setIsBlobUrl(!!videoFile);
  }, [videoFile]);
  
  useEffect(() => {
    // cleanup URL khi unmount hoặc file đổi
    return () => {
      if (videoFile && videoUrl) {
        URL.revokeObjectURL(videoUrl);
      }
    };
  }, [videoFile, videoUrl]);
  const handleSubmit = (e) => {
    e.preventDefault();
    const err = {};
    if (!name.trim()) {
      err.name = "Lesson name is required!";
    } else if (name.trim().length > 255) {
      err.name = "Lesson name must not exceed 255 characters";
    } if (description.trim().length > 500) {
      err.description = "Description must not exceed 500 characters";
    }

    if (type === "video" && !isEdit && !videoFile) {
      err.videoFile = "Please upload a video file";
    }
    if (type === "video" && videoFile && videoFile.size > 500 * 1024 * 1024) {
      err.videoFile = "File size must be less than 500MB";
    }
    if (type === "html" && !htmlContent.trim()) {
      err.htmlContent = "HTML content is required!";
    }
    setErrors(err);

    if (Object.keys(err).length === 0) {
      const payload = {
        type,
        name: name.trim(),
        description: description.trim(),
        isPreview,
        ...(type === "html" ? { htmlContent } : { videoFile }),
      };
      onSubmit(payload, { setErrors });
    } else {
      if (isEdit) {
        message.error("Failed to update lesson");
      } else {
        message.error("Failed to create lesson");
      }
    }
  };

  if (!show) return null;
  if (loading) {
    return (
      <div
        style={{
          position: "fixed",
          zIndex: 9999,
          left: 0,
          top: 0,
          width: "100vw",
          height: "100vh",
          background: "rgba(44,41,99,0.25)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 10px 32px #2224",
            padding: 40,
            minWidth: 320,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Spin size="large" />
          <div style={{ marginTop: 18, fontWeight: 600, color: "#1677ff" }}>
            {isEdit ? "Saving lesson..." : "Creating lesson, please wait..."}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: "fixed",
        zIndex: 9999,
        left: 0,
        top: 0,
        width: "100vw",
        height: "100vh",
        background: "rgba(44,41,99,0.25)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          boxShadow: "0 10px 32px #2224",
          width: "90vw",
          maxWidth: "96vw",
          maxHeight: "95vh",
          overflowY: "auto",
        }}
      >
        <form onSubmit={handleSubmit} autoComplete="off">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              borderBottom: "1px solid #eee",
              padding: "18px 28px",
            }}
          >
            <h5 style={{ margin: 0, fontWeight: 700, flex: 1 }}>
              {isEdit
                ? `Edit lesson${sectionName ? ` in "${sectionName}"` : ""}`
                : `Add new lesson${sectionName ? ` to "${sectionName}"` : ""}`}
            </h5>
            <button
              type="button"
              onClick={onClose}
              style={{
                border: "none",
                background: "none",
                fontSize: 26,
                lineHeight: 1,
                cursor: "pointer",
                marginLeft: 16,
              }}
              aria-label="Close"
            >
              &times;
            </button>
          </div>
          <div style={{ padding: "22px 28px 0 28px" }}>
            {/* Lesson type */}
            <div className="mb-3" style={{ marginBottom: 20 }}>
              <label className="form-label" style={{ fontWeight: 500 }}>
                Lesson type
              </label>
              <select
                className="form-select"
                value={type}
                onChange={(e) => {
                  if (!isEdit) setType(e.target.value);
                }}
                disabled={isEdit}
                style={{
                  background: isEdit ? "#f5f5f5" : "#fff",
                  color: "#222",
                  cursor: isEdit ? "not-allowed" : "pointer",
                  border: isEdit ? "1.5px solid #eee" : "1px solid #ced4da",
                }}
              >
                <option value="html">HTML (Write directly)</option>
                <option value="video">Video (Upload file)</option>
              </select>
            </div>
            {/* Lesson name */}
            <div className="mb-3" style={{ marginBottom: 20 }}>
              <label className="form-label" style={{ fontWeight: 500 }}>
                Lesson name <span style={{ color: "red" }}>*</span>
              </label>
              <input
                className="form-control"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setErrors((err) => ({ ...err, name: undefined }));
                }}
                
                placeholder="Enter lesson name..."
                style={{
                  border: errors.name && errors.name.length ? "1.5px solid #ff4d4f" : "1px solid #ced4da",
                  outline: "none",
                }}
                autoFocus
              />
              {errors.name && <div style={{ color: "#ff4d4f", marginTop: 4, fontSize: 13 }}>{errors.name}</div>}
            </div>
            {/* Description */}
            <div className="mb-3" style={{ marginBottom: 16 }}>
              <label className="form-label" style={{ fontWeight: 500 }}>
                Description
              </label>
              <textarea
                className="form-control"
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  setErrors((err) => ({ ...err, description: undefined }));
                }}
                placeholder="Enter lesson description (optional)..."
                style={{
                  border: errors.description && errors.description.length ? "1.5px solid #ff4d4f" : "1px solid #ced4da",
                  outline: "none",
                }}
              />
              {errors.description && (
                <div style={{ color: "#ff4d4f", marginTop: 4, fontSize: 13 }}>{errors.description}</div>
              )}
            </div>
            {/* HTML content */}
            {(type === "html" || type === "HTML") && (
              <div className="mb-3" style={{ marginBottom: 16 }}>
                <label className="form-label" style={{ fontWeight: 500 }}>
                  HTML Content <span style={{ color: "red" }}>*</span>
                </label>
                <div style={{
                  border: errors.htmlContent && errors.htmlContent.length ? "1.5px solid #ff4d4f" : "1px solid #ced4da",
                  borderRadius: 4,
                }}>
                  <MyQuillWithUpload value={htmlContent} onChange={setHtmlContent} />
                </div>
                {errors.htmlContent && (
                  <div style={{ color: "#ff4d4f", marginTop: 4, fontSize: 13 }}>{errors.htmlContent}</div>
                )}
              </div>
            )}
            {/* Video upload */}
            {type === "video" && (
              <div className="mb-3" style={{ marginBottom: 16 }}>
                <label className="form-label" style={{ fontWeight: 500 }}>
                  {isEdit ? "Select new video file (leave blank to keep the current video)" : "Select video file"}
                </label>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="file"
                    accept="video/*"
                    id="video-upload"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      setVideoFile(e.target.files?.[0] || null);
                      setErrors((err) => ({ ...err, videoFile: undefined }));
                    }}
                  />
                  <label
                    htmlFor="video-upload"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "6px 16px",
                      background: "#fff",
                      color: "#444",
                      border: "1.5px solid #ced4da",
                      borderRadius: 6,
                      cursor: "pointer",
                      fontWeight: 500,
                      fontSize: 15,
                      transition: "border 0.2s",
                      userSelect: "none"
                    }}
                  >
                    <UploadOutlined style={{ fontSize: 18 }} />
                    Upload Video
                  </label>
                  {videoFile && (
                    <>
                      <span style={{ marginLeft: 4, color: "#222" }}>{videoFile.name}</span>
                      <button
                        type="button"
                        onClick={() => setVideoFile(null)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#ff4d4f",
                          fontSize: 18,
                          cursor: "pointer",
                          marginLeft: 2,
                        }}
                        title="Remove video"
                      >
                        ×
                      </button>
                    </>
                  )}
                </div>
                {errors.videoFile && (
                  <div style={{ color: "#ff4d4f", marginTop: 4, fontSize: 13 }}>{errors.videoFile}</div>
                )}
                {videoUrl && (
                  <div style={{ marginTop: 8 }}>
                    <video src={videoUrl} controls style={{ maxWidth: "100%", maxHeight: 200 }} />
                  </div>
                )}
              </div>
            )}
          </div>
          {/* Footer buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 12,
              padding: "14px 28px 22px",
              borderTop: "1px solid #eee",
            }}
          >
            <button
              type="button"
              style={{
                ...BUTTON_STYLE,
                background: "#fff",
                color: PRIMARY_COLOR,
                border: `2px solid ${PRIMARY_COLOR}`,
              }}
              className="btn btn-secondary me-2"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                ...BUTTON_STYLE,
                background: PRIMARY_COLOR,
                color: "#fff",
                border: `2px solid ${PRIMARY_COLOR}`,
              }}
              className="btn btn-primary"
            >
              {isEdit ? "Save changes" : "Create lesson"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
